import { useState, memo } from 'react';
import { Link, useSearchParams, Navigate } from 'react-router-dom';
import { apiResetPassword } from '../api/auth';
import { ApiError } from '../api/client';
import { usePasswordStrength } from '../hooks/usePasswordStrength';
import { getPasswordStrength } from '../hooks/usePasswordStrength';

// ── Strength meter (copied pattern from RegisterPage) ─────────────────────────

const StrengthMeter = memo(function StrengthMeter({ password }: { password: string }) {
  const strength = usePasswordStrength(password);
  if (!password) return null;
  return (
    <div className="strength-meter">
      <div className="strength-bars">
        {([1, 2, 3, 4] as const).map((level) => (
          <div
            key={level}
            className="strength-bar"
            style={{ background: strength.score >= level ? strength.color : 'var(--border)' }}
          />
        ))}
      </div>
      {strength.score > 0 && (
        <span className="strength-label" style={{ color: strength.color }}>
          {strength.label}
        </span>
      )}
      <ul className="strength-rules">
        {strength.rules.map((rule) => (
          <li
            key={rule.key}
            className={`strength-rule ${rule.pass ? 'strength-rule--pass' : 'strength-rule--fail'}`}
          >
            <span className="strength-rule-icon">{rule.pass ? '✓' : '○'}</span>
            {rule.label}
          </li>
        ))}
      </ul>
    </div>
  );
});

// ── Page ──────────────────────────────────────────────────────────────────────

export default memo(function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');

  // No token in URL → invalid entry point
  if (!token) return <Navigate to="/auth/forgot-password" replace />;

  return <ResetForm token={token} />;
});

const ResetForm = memo(function ResetForm({ token }: { token: string }) {
  const [password,        setPassword]        = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading,         setLoading]         = useState(false);
  const [done,            setDone]            = useState(false);
  const [error,           setError]           = useState<string | null>(null);

  const strength = getPasswordStrength(password);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!strength.isAcceptable) {
      setError('Password is too weak — satisfy at least 4 of the 5 rules');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError(null);

    apiResetPassword({ token, password })
      .then(() => setDone(true))
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError
            ? err.status === 400
              ? 'This reset link has expired or already been used.'
              : err.message
            : 'Something went wrong — please try again',
        );
      })
      .finally(() => setLoading(false));
  }

  if (done) {
    return (
      <div className="auth-page">
        <div className="auth-header">
          <h1 className="auth-title">password reset</h1>
        </div>
        <div className="tfa-success-banner" role="status">
          <span className="tfa-success-icon">✓</span>
          Password changed successfully.
        </div>
        <Link to="/login" className="btn btn--primary auth-submit-btn" style={{ marginTop: '1rem' }}>
          sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-header">
        <h1 className="auth-title">reset password</h1>
        <p className="auth-subtitle">Choose a new password for your account.</p>
      </div>

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="form-field">
          <label className="form-label" htmlFor="new-password">new password</label>
          <input
            id="new-password"
            className="form-input"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(null); }}
            disabled={loading}
            autoFocus
          />
          <StrengthMeter password={password} />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="confirm-password">confirm password</label>
          <input
            id="confirm-password"
            className={`form-input${confirmPassword && password !== confirmPassword ? ' form-input--error' : ''}`}
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setError(null); }}
            disabled={loading}
          />
          {confirmPassword && password !== confirmPassword && (
            <span className="form-error">Passwords do not match</span>
          )}
        </div>

        {error && <div className="form-server-error" role="alert">{error}</div>}

        <button
          className="btn btn--primary auth-submit-btn"
          type="submit"
          disabled={loading || !password || !confirmPassword}
        >
          {loading ? (
            <span className="auth-btn-loading">
              <span className="auth-spinner" />
              resetting…
            </span>
          ) : (
            'reset password'
          )}
        </button>
      </form>
    </div>
  );
});
